/**
 * This file is required because we have action(s) which are shared between
 * IssuesV2 and SavedFilters, and importing both into each other results
 * in circular dependency issues (mocha and webpack can't figure it out).
 */
import type { AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';
import * as issuesV2Actions from '../issuesV2/actions';
import * as issuesV2Selectors from '../issuesV2/selectors';
import * as savedFiltersSelectors from './selectors';
import type { ReduxStore } from '../store';
import type { SyncReduxAction } from '../../types';

/**
 * Dispatched after a user selects a previously saved filter in the dropdown.
 */
const setCurrentFilterAction = (currentFilter: number | null): SyncReduxAction<{ currentFilter: number | null }> => ({
  type: 'SAVED_FILTERS_SET_CURRENT_FILTER',
  currentFilter,
});

/**
 * Set current filter updates two things, which is why it is thunk'd
 *    - sets the savedFilters.currentFilter
 *    - sets the issuesV2.filters
 * Look at where this is dispatched, as it is wrapped in a
 * useFetchIssuesAction so that issues are re-fetched when this is dispatched.
 */
export const setCurrentFilter = (currentFilterId: number | null): ThunkAction<void, ReduxStore, never, AnyAction> =>
  (dispatch, getState) => {
    dispatch(setCurrentFilterAction(currentFilterId));
    dispatch(setHasChanged(false)); // track changes per filter
    //  This happens when users set filters to "unfiltered"
    if (currentFilterId === null) {
      dispatch(issuesV2Actions.resetAllFilters());
    } else {
      const state = getState();
      const category = issuesV2Selectors.getIssueCategory(state);
      const currentSort = issuesV2Selectors.getSort(state);
      const currentGroup = issuesV2Selectors.getGrouping(state);
      const currentFilter = savedFiltersSelectors.getCurrentFilterByCategory(state, category);

      if (currentFilter) {
        const { search, ...filter } = currentFilter.filter;
        const { sort, group } = currentFilter;
        dispatch(issuesV2Actions.setFilter(filter));
        //  Set search, if it exists, otherwise this unsets it.
        dispatch(issuesV2Actions.setSearch(search || ''));
        dispatch(issuesV2Actions.setGrouping(group || currentGroup));
        dispatch(issuesV2Actions.setSort(sort || currentSort));
      }
    }
  };

export const setHasChanged = (hasChanged: boolean): SyncReduxAction<{ hasChanged: boolean }> => ({
  type: 'SAVED_FILTERS_SET_HAS_CHANGED',
  hasChanged,
});
